import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { useAssistantStore } from '@/stores/assistantStore';
import { useAuthStore } from '@/stores/authStore';
import {
  useInsightFeedback,
  useInsightSnooze,
  type AssistantInsight,
  type InsightUrgency,
} from '@/hooks/useInsights';
import { recipeForInsightType, useAutomationRecipes } from '@/hooks/useAutomation';

const URGENCY_COLOR: Record<InsightUrgency, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  high: 'warning',
  normal: 'info',
  low: 'default',
};

const SNOOZE_MINUTES = 24 * 60; // 1 day

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** Render i18n interpolation params, formatting ISO timestamps to the active locale. */
function displayParams(
  params: Record<string, unknown>,
  locale: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const fmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  for (const [key, value] of Object.entries(params)) {
    out[key] =
      typeof value === 'string' && ISO_DATE.test(value) ? fmt.format(new Date(value)) : value;
  }
  return out;
}

export function InsightCard({ insight }: { insight: AssistantInsight }) {
  const { t, i18n } = useTranslation('assistant');
  const navigate = useNavigate();
  const openInsight = useAssistantStore((s) => s.openInsight);
  const feedback = useInsightFeedback();
  const snooze = useInsightSnooze();
  const [whyOpen, setWhyOpen] = useState(false);

  const canAutomate = useAuthStore((s) => s.hasPermission('automation.rules.manage'));
  const recipes = useAutomationRecipes(canAutomate);
  const matchingRecipe = canAutomate
    ? recipeForInsightType(recipes.data, insight.insightType)
    : undefined;
  const busy = feedback.isPending || snooze.isPending;
  const params = displayParams(insight.messageParams, i18n.language);
  const facts = insight.evidence?.facts ?? {};
  const factParams = displayParams(facts, i18n.language);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1 }}>
            {t(insight.titleKey, params)}
          </Typography>
          <Chip
            size="small"
            color={URGENCY_COLOR[insight.urgency]}
            label={t(`inbox.urgency.${insight.urgency}`)}
          />
        </Box>

        <Typography variant="body2" color="text.secondary">
          {t(insight.bodyKey, params)}
        </Typography>

        {/* Why? — evidence */}
        <Box>
          <Button
            size="small"
            color="inherit"
            onClick={() => setWhyOpen((v) => !v)}
            endIcon={whyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none', px: 0.5 }}
            aria-expanded={whyOpen}
          >
            {t('inbox.why')}
          </Button>
          <Collapse in={whyOpen} unmountOnExit>
            <Box sx={{ pl: 1, pt: 0.5 }}>
              {Object.entries(factParams).map(([key, value]) => (
                <Typography key={key} variant="caption" component="div" color="text.secondary">
                  {key}: {String(value)}
                </Typography>
              ))}
              {insight.deepLink && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => navigate(insight.deepLink ?? '')}
                  sx={{ mt: 0.5, display: 'inline-block' }}
                >
                  {t('inbox.actions.viewTarget')}
                </Link>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={() => openInsight(insight.id, String(insight.ruleVersion))}
          >
            {t('inbox.actions.open')}
          </Button>
          <Button
            size="small"
            color="inherit"
            disabled={busy}
            onClick={() => snooze.mutate({ id: insight.id, minutes: SNOOZE_MINUTES })}
          >
            {t('inbox.actions.snooze')}
          </Button>
          <Button
            size="small"
            color="inherit"
            disabled={busy}
            onClick={() => feedback.mutate({ id: insight.id, kind: 'not_relevant' })}
          >
            {t('inbox.actions.notRelevant')}
          </Button>
          <Button
            size="small"
            color="inherit"
            disabled={busy}
            onClick={() => feedback.mutate({ id: insight.id, kind: 'mute_type' })}
          >
            {t('inbox.actions.mute')}
          </Button>
          {matchingRecipe && (
            <Button
              size="small"
              color="inherit"
              onClick={() =>
                navigate(
                  `/settings?tab=automations&create=${encodeURIComponent(matchingRecipe.id)}&insightId=${encodeURIComponent(insight.id)}&insightType=${encodeURIComponent(insight.insightType)}`,
                )
              }
            >
              {t('automateThis', { ns: 'automation' })}
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
